import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
const Lightbox = lazy(() => import('yet-another-react-lightbox'))
import {
  FaWhatsapp,
  FaChevronDown,
  FaInstagram,
  FaAward,
  FaClock,
  FaPlane,
  FaCheckCircle,
  FaPlayCircle,
  FaBolt,
  FaRoute,
  FaEnvelope,
  FaQuestionCircle,
  FaArrowRight
} from 'react-icons/fa'
import Header from './components/Header'
import Footer from './components/Footer'
import { WHATSAPP_NUMBER } from './config/navItems'
import { useLanguage } from './i18n'

// Automatic check: only show popup if there are upcoming events
const hasUpcomingEvent = () => {
  const eventEndDates = [
    'Feb 13, 2026',   // Tunnel Jam
    'Jun 29, 2025',   // TunnelCamp Madrid June
    'Jul 13, 2025',   // TunnelCamp Madrid July
    'Aug 10, 2025',   // TunnelCamp Madrid August
    'Sep 5, 2025',    // TunnelCamp Finland
  ]
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return eventEndDates.some(dateStr => {
    const eventDate = new Date(dateStr)
    eventDate.setHours(0, 0, 0, 0)
    return eventDate > today
  })
}

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [showEventPopup, setShowEventPopup] = useState(hasUpcomingEvent())
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interestLevel: '',
    serviceType: '',
    message: ''
  })
  const heroRef = useRef(null)
  const { t, tArray } = useLanguage()

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY)
    }
    window.addEventListener('scroll', handleScroll)

    const adjustHeroPadding = () => {
      if (heroRef.current) {
        const navHeight = document.querySelector('nav')?.offsetHeight || 0
        heroRef.current.style.paddingTop = `${navHeight}px`
      }
    }

    adjustHeroPadding()
    window.addEventListener('resize', adjustHeroPadding)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', adjustHeroPadding)
    }
  }, [])

  const services = [
    {
      title: t('home.services.tunnelCoaching.title'),
      subtitle: t('home.services.tunnelCoaching.subtitle'),
      description: t('home.services.tunnelCoaching.description'),
      extraInfo: t('home.services.tunnelCoaching.extraInfo'),
      price: "From 99€/session",
      includedPrice: "From 150€/h",
      icon: <FaPlane className="w-12 h-12 text-orange-500 mb-4" />,
      features: tArray('home.services.tunnelCoaching.features')
    },
    {
      title: t('home.services.tunnelCamp.title'),
      description: t('home.services.tunnelCamp.description'),
      extraInfo: t('home.services.tunnelCamp.extraInfo'),
      price: "From 150€",
      includedPrice: "From 150€/h",
      additionalInfo: t('home.services.tunnelCamp.additionalInfo'),
      icon: <FaBolt className="w-12 h-12 text-orange-500 mb-4" />,
      features: tArray('home.services.tunnelCamp.features')
    }
  ]

  const stats = [
    { value: "100h+", label: t('home.stats.flightHours'), icon: <FaClock className="text-orange-500" /> },
    { value: "10+", label: t('home.stats.yearsExperience'), icon: <FaAward className="text-orange-500" /> },
    { value: "2000+", label: t('home.stats.jumpsCompleted'), icon: <FaPlane className="text-orange-500" /> }
  ]

  const topFAQs = tArray('home.faq.questions')

  const instagramPosts = tArray('home.gallery.posts').map((post, index) => ({
    ...post,
    id: ["DENxLIhIoxr", "DCl3ZPKOsyO", "DB9aockugLY", "DE9kGuJutgM", "DBynM0QMGyB", "C-509FOuiaq", "C5yKuwiMkDG", "C-uVX2WOun-"][index]
  }))

  const handleEventRedirect = () => {
    window.location.href = '/camps'
    setShowEventPopup(false)
  }

  const handleWhatsAppRedirect = (message, includeFormData = false) => {
    let fullMessage = message

    if (includeFormData && formData.name) {
      fullMessage += `\n\nContact Information:`
      if (formData.name) fullMessage += `\nName: ${formData.name}`
      if (formData.email) fullMessage += `\nEmail: ${formData.email}`
      if (formData.interestLevel) fullMessage += `\nInterested in Level: ${formData.interestLevel}`
      if (formData.serviceType) fullMessage += `\nService Type: ${formData.serviceType}`
      if (formData.message) fullMessage += `\nMessage: ${formData.message}`
    }

    const encodedMessage = encodeURIComponent(fullMessage)
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank')
  }

  const processSteps = tArray('home.process.steps')

  return (
    <div className="font-sans bg-gray-900">
      {/* WhatsApp Float Button */}
      <motion.a
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        href={`https://wa.me/${WHATSAPP_NUMBER}`}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-6 right-6 bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-full shadow-xl hover:shadow-orange-500/50 transition-all z-50 hover:scale-110"
        aria-label="Contact us on WhatsApp"
      >
        <FaWhatsapp size={28} />
      </motion.a>

      {/* New Event Announcement Popup */}
      <AnimatePresence>
        {showEventPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowEventPopup(false)
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="relative max-w-md w-full rounded-2xl overflow-hidden shadow-2xl border border-orange-500/30"
            >
              <div className="absolute inset-0 z-0">
                <img
                  src="/popup-desktop-bg.jpg"
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-blue-900/80 to-gray-900/95"></div>
              </div>

              <button
                onClick={() => setShowEventPopup(false)}
                className="absolute top-4 right-4 z-20 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all"
                aria-label="Close popup"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="relative z-10 bg-gradient-to-r from-orange-500/90 to-red-500/90 p-6 text-white text-center">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="inline-block mb-3"
                >
                  <span className="text-4xl">🎉</span>
                </motion.div>
                <h2 className="text-2xl md:text-3xl font-black">{t('home.popup.title')}</h2>
              </div>

              <div className="relative z-10 p-6 text-center">
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="mb-4"
                >
                  <span className="text-5xl">🪂</span>
                </motion.div>

                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  {t('home.popup.eventTitle')}
                </h3>
                <p className="text-lime-400 font-semibold mb-4">
                  {t('home.popup.eventSubtitle')}
                </p>

                <p className="text-gray-300 mb-6">
                  {t('home.popup.eventDescription')}
                </p>

                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-4 mb-6 border border-orange-500/30">
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-300">
                    <span className="flex items-center">
                      <FaPlane className="text-orange-500 mr-2" />
                      {t('home.popup.anyTunnel')}
                    </span>
                    <span className="flex items-center">
                      <FaClock className="text-orange-500 mr-2" />
                      {t('home.popup.flexible')}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      setShowEventPopup(false)
                      window.location.href = '/camps'
                    }}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-xl font-bold hover:shadow-lg hover:shadow-orange-500/30 transition-all transform hover:scale-105 flex items-center justify-center"
                  >
                    <FaArrowRight className="mr-2" />
                    {t('home.popup.viewEvents')}
                  </button>
                  <button
                    onClick={() => setShowEventPopup(false)}
                    className="flex-1 bg-gray-700 text-gray-300 py-3 px-6 rounded-xl font-bold hover:bg-gray-600 transition-all"
                  >
                    {t('home.popup.maybeLater')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header Component */}
      <Header />

      {/* Hero Section */}
      <div
        ref={heroRef}
        className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex flex-col justify-center"
      >
        <div className="absolute inset-0 z-0 opacity-50">
          <picture>
            <source srcSet="/cabecera.webp" type="image/webp" />
            <img
              src="/cabecera.jpg"
              alt="Wind tunnel flying experience background"
              className="w-full h-full object-cover"
              width="1920"
              height="1080"
              loading="eager"
              fetchpriority="high"
            />
          </picture>
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-transparent to-gray-900"></div>

        <div className="relative z-20 flex-grow flex items-center justify-center">
          <div className="text-center px-4 space-y-6 md:space-y-8 max-w-4xl">
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold leading-tight"
            >
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                {t('home.hero.title')}
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="max-w-2xl mx-auto bg-gray-900/80 p-6 md:p-8 rounded-2xl backdrop-blur-sm border border-orange-500/20"
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                  {t('home.hero.subtitle')}
                </span>
              </h2>
              <p className="text-base md:text-lg text-gray-200">
                {t('home.hero.description')}
              </p>
            </motion.div>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center"
            >
              <button
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="group relative overflow-hidden bg-gradient-to-r from-orange-500 to-red-500 px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105"
              >
                <span className="relative z-10 text-white group-hover:text-white">
                  {t('home.hero.cta')}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <a
                href="/roadmap"
                className="group relative overflow-hidden bg-white text-orange-500 px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:shadow-lg transition-all transform hover:scale-105"
              >
                <span className="relative z-10">
                  {t('home.hero.ctaSecondary')}
                </span>
              </a>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 text-center pb-8">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <FaChevronDown className="text-white/50 w-6 h-6 mx-auto" />
          </motion.div>
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-gray-900 to-blue-900">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-block p-4 bg-gray-800/50 rounded-full mb-4 border border-orange-500/20">
                  {stat.icon}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{stat.value}</h3>
                <p className="text-gray-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" aria-label="Our services" className="py-12 md:py-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-10 md:mb-16 text-orange-500">
            {t('home.services.title')}
          </h2>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -10 }}
                className="relative bg-white p-6 md:p-8 rounded-2xl shadow-xl hover:shadow-orange-500/20 transition-all border border-gray-200 flex flex-col"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent rounded-2xl opacity-0 hover:opacity-100 transition-opacity" />

                <div className="flex justify-center mb-6">
                  {service.icon}
                </div>

                <div className="flex flex-col items-center md:flex-row md:justify-between md:items-center mb-4">
                  <div className="flex flex-col sm:flex-row items-center gap-2 text-center md:text-left">
                    <h3 className="text-2xl font-bold text-gray-800">{service.title}</h3>
                    {service.subtitle && (
                      <span className="text-red-500 text-sm">{service.subtitle}</span>
                    )}
                  </div>
                </div>

                <div className="flex-grow">
                  <p className="text-gray-600 mb-4">{service.description}</p>

                  {service.extraInfo && (
                    <p className="text-gray-800 font-semibold mb-4">{service.extraInfo}</p>
                  )}

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <div className="text-orange-500 mr-2">•</div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-2 mb-6">
                    <p className="text-orange-500 text-lg">{service.includedPrice}</p>
                  </div>

                  {service.additionalInfo && (
                    <p className="text-red-500 text-sm text-center">
                      {service.additionalInfo.split('\n').map((line, i) => (
                        <span key={i} className="block">{line}</span>
                      ))}
                    </p>
                  )}
                </div>

                <div className="mt-auto">
                  <button
                    onClick={() => {
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 mt-4"
                  >
                    <span>{t('home.services.bookButton')}</span>
                    <FaWhatsapp size={20} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 md:py-16 bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6">
              {t('home.faq.title')} <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">{t('home.faq.titleHighlight')}</span>
            </h2>
            <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto">
              {t('home.faq.description')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-1 gap-6 md:gap-8 max-w-4xl mx-auto">
            {topFAQs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-800/50 rounded-2xl p-6 md:p-8 backdrop-blur-sm border border-orange-500/20"
              >
                <div className="flex items-start mb-4">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 p-3 rounded-xl mr-4 flex-shrink-0">
                    <FaQuestionCircle className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                      {faq.question}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <a
              href="/FAQ"
              className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full text-lg font-bold hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105"
            >
              <FaQuestionCircle className="mr-3" />
              {t('home.faq.viewAll')}
              <FaArrowRight className="ml-3" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Coach Section */}
      <section id="coach" className="py-16 md:py-20 bg-gradient-to-b from-gray-100 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="mb-8">
            <picture>
              <source srcSet="/coach-photo.webp" type="image/webp" />
              <img
                src="/coach-photo.jpg"
                alt="Professional wind tunnel coach with over 10 years of experience in indoor skydiving"
                className="w-48 h-48 md:w-64 md:h-64 object-cover rounded-full mx-auto border-4 border-orange-500 shadow-xl shadow-orange-500/20"
                width="256"
                height="256"
                loading="lazy"
              />
            </picture>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              {t('home.coach.title')}
            </span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            {t('home.coach.description')}
          </p>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
              <h3 className="text-xl md:text-2xl font-bold text-orange-500 mb-4">{t('home.coach.experience.title')}</h3>
              <ul className="text-gray-600 space-y-2">
                {tArray('home.coach.experience.items').map((item, idx) => (
                  <li key={idx}>✓ {item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
              <h3 className="text-xl md:text-2xl font-bold text-orange-500 mb-4">{t('home.coach.methodology.title')}</h3>
              <ul className="text-gray-600 space-y-2">
                {tArray('home.coach.methodology.items').map((item, idx) => (
                  <li key={idx}>✓ {item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                const contactSection = document.getElementById('contact')
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' })
                }
              }}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 md:px-8 py-3 md:py-4 rounded-full hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105"
            >
              {t('home.coach.connectButton')}
            </button>
            <a
              href="/roadmap"
              className="bg-white border-2 border-orange-500 text-orange-500 px-6 md:px-8 py-3 md:py-4 rounded-full hover:shadow-lg transition-all transform hover:scale-105 flex items-center justify-center"
            >
              {t('home.coach.roadmapButton')} <FaRoute className="ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Dynamic Video Section */}
      <section className="min-h-screen relative overflow-hidden flex items-center">
        <div className="absolute inset-0 z-0">
          <picture>
            <source srcSet="/tunnel-background.webp" type="image/webp" />
            <img
              src="/tunnel-background.jpg"
              alt="Wind tunnel facility background"
              className="w-full h-full object-cover"
              width="1920"
              height="1080"
              loading="lazy"
            />
          </picture>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/50 to-gray-900"></div>
        <div className="relative z-10 w-full py-16">
          <div className="text-center text-white max-w-4xl mx-auto px-4">
            <motion.h3
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="text-3xl sm:text-4xl md:text-6xl font-bold mb-6"
            >
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                {t('home.video.title')}
              </span>
            </motion.h3>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl mb-8"
            >
              {t('home.video.description')}
            </motion.p>
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-lg font-bold hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105"
            >
              {t('home.video.cta')}
            </motion.button>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-12 md:py-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-16">
            <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              {t('home.process.title')}
            </span>
          </h2>

          {/* Desktop version */}
          <div className="hidden md:block">
            <div className="relative flex flex-col items-center">
              <div className="absolute inset-y-0 w-1 bg-orange-500/20"></div>

              {processSteps.map((item, index) => (
                <div key={index} className="flex w-full mb-12 items-center">
                  {index % 2 === 0 && (
                    <>
                      <div className="w-1/2 pr-8">
                        <div className="text-right">
                          <h3 className="text-xl font-bold text-orange-500 mb-2">{item.title}</h3>
                          <p className="text-gray-600">{item.description}</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-full p-4 shadow-lg z-10 border border-orange-500/20">
                        {index === processSteps.length - 1 ? <FaAward className="text-orange-500" /> : <FaCheckCircle className="text-orange-500" />}
                      </div>
                      <div className="w-1/2"></div>
                    </>
                  )}

                  {index % 2 === 1 && (
                    <>
                      <div className="w-1/2"></div>
                      <div className="bg-white rounded-full p-4 shadow-lg z-10 border border-orange-500/20">
                        <FaCheckCircle className="text-orange-500" />
                      </div>
                      <div className="w-1/2 pl-8">
                        <div className="text-left">
                          <h3 className="text-xl font-bold text-orange-500 mb-2">{item.title}</h3>
                          <p className="text-gray-600">{item.description}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mobile version */}
          <div className="md:hidden">
            <div className="space-y-8">
              {processSteps.map((item, index) => (
                <div key={index} className="flex items-start bg-white rounded-lg p-4 shadow-md">
                  <div className="bg-orange-100 rounded-full p-3 mr-4">
                    {index === processSteps.length - 1 ? <FaAward className="text-orange-500 text-2xl" /> : <FaCheckCircle className="text-orange-500 text-2xl" />}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-orange-500 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="/roadmap"
              className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-lg font-bold hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105"
            >
              {t('home.process.viewRoadmap')} <FaRoute className="ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-12 md:py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              {t('home.gallery.title')}
            </span>
          </h2>

          <p className="text-white text-center text-base md:text-lg mb-8 md:mb-10 max-w-2xl mx-auto">
            {t('home.gallery.description')}
          </p>

          <style>
            {`
              .instagram-post-container {
                position: relative;
                width: 100%;
                padding-bottom: 100%;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 10px 25px -5px rgba(0,0,0,0.3);
                transition: all 0.3s ease;
              }

              .instagram-post-container:hover {
                transform: translateY(-5px);
                box-shadow: 0 15px 30px -5px rgba(0,0,0,0.4);
              }

              .instagram-post-container iframe {
                position: absolute;
                top: -54px;
                left: 0;
                width: 100%;
                height: calc(100% + 54px);
                border: none;
                overflow: hidden;
              }

              .post-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.7) 100%);
                opacity: 0;
                transition: opacity 0.3s ease;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                padding: 20px;
                z-index: 10;
                pointer-events: none;
              }

              .instagram-post-container:hover .post-overlay {
                opacity: 1;
              }

              .play-button {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 50px;
                height: 50px;
                background-color: rgba(255, 255, 255, 0.2);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.3s ease;
                z-index: 5;
              }

              .instagram-post-container:hover .play-button {
                background-color: rgba(255, 255, 255, 0.4);
                transform: translate(-50%, -50%) scale(1.1);
              }

              .post-wrapper {
                position: relative;
                transform: translateY(0);
                transition: transform 0.3s ease;
              }

              .post-wrapper:hover {
                transform: translateY(-8px);
              }

              @keyframes pulse {
                0% { transform: scale(1); opacity: 0.8; }
                50% { transform: scale(1.1); opacity: 1; }
                100% { transform: scale(1); opacity: 0.8; }
              }

              .pulse-animation {
                animation: pulse 2s infinite;
              }
            `}
          </style>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {instagramPosts.map((post, index) => (
              <div key={index} className="post-wrapper">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="rounded-xl overflow-hidden shadow-xl bg-white"
                >
                  <div className="instagram-post-container">
                    <iframe
                      src={`https://www.instagram.com/p/${post.id}/embed`}
                      className="w-full h-full"
                      title={post.title}
                      frameBorder="0"
                      scrolling="no"
                      allowFullScreen={true}
                      loading="lazy"
                    ></iframe>
                    <div className="play-button pulse-animation">
                      <FaPlayCircle className="text-white text-xl md:text-3xl" />
                    </div>
                    <div className="post-overlay">
                      <p className="text-white font-medium">
                        {post.caption}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>

          {/* Follow Button */}
          <div className="text-center mt-10 md:mt-16">
            <div className="max-w-xl mx-auto bg-gray-800/50 p-5 md:p-6 rounded-2xl backdrop-blur-sm border border-orange-500/20 mb-6 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">
                {t('home.gallery.followTitle')} <span className="text-orange-500">{t('home.gallery.followHighlight')}</span>
              </h3>
              <p className="text-gray-300 mb-5 md:mb-6">
                {t('home.gallery.followDescription')}
              </p>
              <a
                href="https://www.instagram.com/alexmatei.fly?igsh=dTZxZXEwZ2Fpbzhp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 md:px-8 py-3 md:py-4 rounded-full hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105"
              >
                <FaInstagram className="text-xl" />
                <span>{t('home.gallery.followButton')}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 md:py-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl p-6 md:p-12 text-gray-800 border border-gray-200 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8">
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                {t('home.contact.title')}
              </span>
            </h2>
            <p className="text-center text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto">
              {t('home.contact.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105 flex items-center justify-center text-lg font-bold"
              >
                <FaEnvelope className="mr-2" />
                {t('home.contact.contactButton')}
              </a>

              <button
                onClick={() => handleWhatsAppRedirect("Hi! I'm interested in wind tunnel coaching. Can we talk?")}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full transition-all transform hover:scale-105 flex items-center justify-center text-lg font-bold"
              >
                <FaWhatsapp className="mr-2" />
                {t('home.contact.whatsappButton')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default App;
